import { normalize } from 'normalizr';

import type { PartialPricingEngineOverrides } from '@frontend/api';

import * as SCHEMA from 'store/quotes/schema';

import { notify } from 'utils/reduxUtils';

import type {
  ComponentInterrogationStatuses,
  ComponentProcessingStatuses,
} from './reducer';
import * as types from './types';

export const createAddOn = {
  error: notify(
    (error) => ({
      type: types.ADD_ON_CREATE_ERROR,
      payload: error,
    }),
    "Can't create add-on",
  ),
  request: ({ componentId, data }) => ({
    type: types.ADD_ON_CREATE_REQUEST,
    payload: { componentId, data },
  }),
  success: () => ({
    type: types.ADD_ON_CREATE_SUCCESS,
  }),
};

export const deleteAddOn = {
  error: notify(
    (error) => ({
      type: types.ADD_ON_DELETE_ERROR,
      payload: error,
    }),
    "Can't delete add-on",
  ),
  request: ({ addOnId }) => ({
    type: types.ADD_ON_DELETE_REQUEST,
    payload: { addOnId },
  }),
};

export const initialBriefComponentFetch = {
  request: ({ quoteItemId }) => ({
    type: types.FETCH_INITIAL_BRIEF_COMPONENTS_REQUEST,
    payload: { quoteItemId },
  }),
  success: ({ briefComponents, quoteItemId }) => ({
    type: types.FETCH_INITIAL_BRIEF_COMPONENTS_SUCCESS,
    payload: {
      quoteItemId,
      ...normalize(briefComponents, [SCHEMA.quoteItemBriefComponent]),
    },
  }),
  error: ({ quoteItemId }) => ({
    type: types.FETCH_INITIAL_BRIEF_COMPONENTS_ERROR,
    payload: { quoteItemId },
  }),
};

export const lockQuoteItem = ({ quoteItemId }) => ({
  type: types.LOCK_QUOTE_ITEM,
  payload: { quoteItemId },
});

export const takeOverQuoteItem = {
  request: ({ quoteItemId, replaceHistory }) => ({
    type: types.TAKE_OVER_QUOTE_ITEM_REQUEST,
    payload: { quoteItemId, replaceHistory },
  }),
};

export const expandQuoteItem = {
  request: ({ quoteItemId, replaceHistory }) => ({
    type: types.EXPAND_QUOTE_ITEM_REQUEST,
    payload: { quoteItemId, replaceHistory },
  }),
  success: ({ quoteItem }) => ({
    type: types.EXPAND_QUOTE_ITEM_SUCCESS,
    payload: {
      quoteItemId: quoteItem.id,
      ...normalize(quoteItem, SCHEMA.quoteItem),
    },
  }),
  error: ({ quoteItemId }) => ({
    type: types.EXPAND_QUOTE_ITEM_ERROR,
    payload: { quoteItemId },
  }),
};

export const collapseQuoteItem = {
  request: () => ({
    type: types.COLLAPSE_QUOTE_ITEM,
  }),
};

export const receiveComponent = (data) => ({
  type: types.RECEIVE_COMPONENT,
  payload: normalize(data, SCHEMA.quoteItemComponent),
});

export const receiveComponents = (data) => ({
  type: types.RECEIVE_COMPONENTS,
  payload: normalize(data, [SCHEMA.quoteItemComponent]),
});

export const receiveQuoteWorkflow = ({ quoteWorkflow }) => ({
  type: types.RECEIVE_QUOTE_WORKFLOW,
  payload: { quoteWorkflow },
});

export const briefComponent = {
  add: ({ component, quoteItemId }) => ({
    type: types.BRIEF_COMPONENT_ADD,
    payload: { component, quoteItemId },
  }),
  receive: ({ briefComponent }) => ({
    type: types.BRIEF_COMPONENT_RECEIVE,
    payload: normalize(briefComponent, SCHEMA.quoteItemBriefComponent),
  }),
  receiveMultiple: ({ briefComponents }) => ({
    type: types.BRIEF_COMPONENT_RECEIVE_MULTIPLE,
    payload: normalize(briefComponents, [SCHEMA.quoteItemBriefComponent]),
  }),
  refresh: ({ componentId }) => ({
    type: types.BRIEF_COMPONENT_REFRESH,
    payload: { componentId },
  }),
  refreshMultiple: ({ componentIds, quoteItemId }) => ({
    type: types.BRIEF_COMPONENT_REFRESH_MULTIPLE,
    payload: { componentIds, quoteItemId },
  }),
};

export const createQuantity = {
  error: notify(
    (error) => ({
      type: types.CREATE_QUANTITY_ERROR,
      payload: error,
    }),
    "Can't add quantity to quote item",
  ),
  request: (componentId, quantity) => ({
    type: types.CREATE_QUANTITY_REQUEST,
    payload: { componentId, quantity },
  }),
};

export const deleteQuantity = {
  error: notify(
    (error) => ({
      type: types.DELETE_QUANTITY_ERROR,
      payload: error,
    }),
    "Can't update quote item quantity",
  ),
  request: (componentId, quantityId) => ({
    type: types.DELETE_QUANTITY_REQUEST,
    payload: { componentId, quantityId },
  }),
};

export const receiveQuantities = (data) => ({
  type: types.RECEIVE_QUANTITIES,
  payload: normalize(data, [SCHEMA.quoteItemQuantity]),
});

export const updateAddOn = {
  error: notify(
    (error) => ({
      type: types.ADD_ON_UPDATE_ERROR,
      payload: error,
    }),
    "Can't update add-on",
  ),
  request: ({ addOnId, data }) => ({
    type: types.ADD_ON_UPDATE_REQUEST,
    payload: { addOnId, data },
  }),
  success: (data) => ({
    type: types.ADD_ON_UPDATE_SUCCESS,
    payload: normalize(data, SCHEMA.quoteItemAddOn),
  }),
};

export const updateAddOnCell = {
  error: notify(
    (error) => ({
      type: types.ADD_ON_CELL_UPDATE_ERROR,
      payload: error,
    }),
    "Can't update add-on cell",
  ),
  request: ({ addOnCellId, data, cb }) => ({
    type: types.ADD_ON_CELL_UPDATE_REQUEST,
    payload: { addOnCellId, data, cb },
  }),
  success: (data) => ({
    type: types.ADD_ON_CELL_UPDATE_SUCCESS,
    payload: normalize(data, SCHEMA.quoteItemAddOnCell),
  }),
};

export const updateAddOnOverrides = {
  error: notify(
    ({ addOnId }) => ({
      type: types.UPDATE_ADD_ON_OVERRIDES_ERROR,
      payload: { addOnId },
    }),
    "Can't update add-on",
  ),
  request: ({ addOnId, overrides, notes }) => ({
    type: types.UPDATE_ADD_ON_OVERRIDES_REQUEST,
    payload: { addOnId, overrides, notes },
  }),
  success: ({ addOnId }) => ({
    type: types.UPDATE_ADD_ON_OVERRIDES_SUCCESS,
    payload: { addOnId },
  }),
};

export const updateQuantity = {
  error: notify(
    (error) => ({
      type: types.UPDATE_QUANTITY_ERROR,
      payload: error,
    }),
    "Can't update quote item quantity",
  ),
  request: (quantityId, data) => ({
    type: types.UPDATE_QUANTITY_REQUEST,
    payload: { quantityId, data },
  }),
  success: (data) => ({
    type: types.UPDATE_QUANTITY_SUCCESS,
    payload: normalize(data, SCHEMA.quoteItemQuantity),
  }),
};

export const updatePartFilename = {
  error: notify(
    (error, componentId) => ({
      type: types.UPDATE_PART_FILENAME_ERROR,
      payload: { error, componentId },
    }),
    "Can't update filename",
  ),
  request: ({
    componentId,
    partId,
  }: {
    componentId: number;
    partId: string;
  }) => ({
    type: types.UPDATE_PART_FILENAME_REQUEST,
    payload: { componentId, partId },
  }),
  success: ({ componentId }: { componentId: number }) => ({
    type: types.UPDATE_PART_FILENAME_SUCCESS,
    payload: { componentId },
  }),
};

export const updateQuantities = {
  error: notify(
    (error) => ({
      type: types.UPDATE_QUANTITIES_ERROR,
      payload: error,
    }),
    "Can't update quote item quantity",
  ),
  request: (componentId, data) => ({
    type: types.UPDATE_QUANTITIES_REQUEST,
    payload: { componentId, data },
  }),
  success: (data) => ({
    type: types.UPDATE_QUANTITIES_SUCCESS,
    payload: normalize(data, [SCHEMA.quoteItemQuantity]),
  }),
};

export const updateCell = {
  error: notify(
    ({ error, cellId, quantityId }) => ({
      type: types.UPDATE_CELL_ERROR,
      payload: {
        error,
        cellId,
        quantityId,
      },
    }),
    "Can't update operation price",
  ),
  request: ({ cellId, quantityId, data, cb }) => ({
    type: types.UPDATE_CELL_REQUEST,
    payload: { cellId, quantityId, data, cb },
  }),
  success: (data) => ({
    type: types.UPDATE_CELL_SUCCESS,
    payload: {
      ...normalize(data, SCHEMA.quoteItemCell),
      cellId: data.id,
      quantityId: data.quantityId,
    },
  }),
};

export const updateOperation = {
  error: notify(
    (error) => ({
      type: types.UPDATE_OPERATION_ERROR,
      payload: error,
    }),
    "Can't update operation",
  ),
  request: (operationId, data) => ({
    type: types.UPDATE_OPERATION_REQUEST,
    payload: { operationId, data },
  }),
  success: (payload) => ({
    type: types.UPDATE_OPERATION_SUCCESS,
    payload: {
      ...normalize(payload.data, [SCHEMA.quoteItemOperation]),
      operationId: payload.operationId,
    },
  }),
};

export const receiveAddOns = (data) => ({
  type: types.RECEIVE_ADD_ONS,
  payload: normalize(data, [SCHEMA.quoteItemAddOn]),
});

export const createOperation = {
  error: notify(
    (error) => ({
      type: types.CREATE_OPERATION_ERROR,
      payload: error,
    }),
    "Can't create operation",
  ),
  request: (componentId, data) => ({
    type: types.CREATE_OPERATION_REQUEST,
    payload: { componentId, data },
  }),
  success: (category) => ({
    type: types.CREATE_OPERATION_SUCCESS,
    payload: { category },
  }),
};

export const deleteOperation = {
  error: notify(
    (error) => ({
      type: types.DELETE_OPERATION_ERROR,
      payload: error,
    }),
    "Can't delete operation",
  ),
  request: (componentId, operationId) => ({
    type: types.DELETE_OPERATION_REQUEST,
    payload: { componentId, operationId },
  }),
};

export const reorderOperations = {
  error: notify(
    (error) => ({
      type: types.REORDER_OPERATIONS_ERROR,
      payload: error,
    }),
    "Can't re-order operations",
  ),
  request: (componentId, oldPosition, nextPosition) => ({
    type: types.REORDER_OPERATIONS_REQUEST,
    payload: { componentId, oldPosition, nextPosition },
  }),
  success: (data) => ({
    type: types.REORDER_OPERATIONS_SUCCESS,
    payload: normalize(data, [SCHEMA.quoteItemOperation]),
  }),
};

export const reorderAddOns = {
  error: notify(
    (error) => ({
      type: types.REORDER_ADD_ONS_ERROR,
      payload: error,
    }),
    "Can't re-order add-ons",
  ),
  request: ({ componentId, oldPosition, nextPosition }) => ({
    type: types.REORDER_ADD_ONS_REQUEST,
    payload: { componentId, oldPosition, nextPosition },
  }),
  success: () => ({
    type: types.REORDER_ADD_ONS_SUCCESS,
  }),
};

export const updateQuoteItem = {
  request: (payload) => ({
    type: types.UPDATE_QUOTE_ITEM_REQUEST,
    payload,
  }),
  success: (payload) => ({
    type: types.UPDATE_QUOTE_ITEM_SUCCESS,
    payload: {
      ...normalize(payload.quoteItem, SCHEMA.quoteItem),
      data: payload.data,
    },
  }),
  error: notify(
    (error) => ({
      type: types.UPDATE_QUOTE_ITEM_ERROR,
      payload: error,
    }),
    "Can't update quote item",
  ),
};

export const refreshComponent = ({ componentId, params = {} }) => ({
  type: types.REFRESH_COMPONENT,
  payload: { componentId, params },
});

export const refreshQuoteItem = ({ quoteItemId, shouldRecalculate }) => ({
  type: types.REFRESH_QUOTE_ITEM,
  payload: { quoteItemId, shouldRecalculate },
});

export const receiveQuoteItem = (payload) => ({
  type: types.RECEIVE_QUOTE_ITEM,
  payload: normalize(payload, SCHEMA.quoteItem),
});

export const receiveQuoteItems = (payload) => ({
  type: types.RECEIVE_QUOTE_ITEMS,
  payload: normalize(payload, [SCHEMA.quoteItem]),
});

/**
 * This action was introduced in this ticket: https://paperlessparts.atlassian.net/browse/PP-17561
 * It was introduced as a way to maintain backwards compatibility with the way quote items were managed before the implementation of this card where
 *   the quote item and quote entities were separated from each other.
 * After this card, the frontend knows which quote items belong to a quote by the quote items' quoteId attribute. It was found that completely
 *  removing the deleted quote items from the entities store triggered a bug in the table virtualization library, virtuoso, where a quote item that was deleted
 *  is trying to render, but the selectors are all throwing attribute access errors because the quote item no longer exists in the store.
 */
export const removeQuoteItems = (payload) => ({
  type: types.REMOVE_QUOTE_ITEMS,
  payload: normalize(
    payload.map((qi) => ({ ...qi, quoteId: null })),
    [SCHEMA.quoteItem],
  ),
});

export const updateComponent = {
  request: (payload) => ({
    type: types.UPDATE_COMPONENT_REQUEST,
    payload,
  }),
  success: (payload) => ({
    type: types.UPDATE_COMPONENT_SUCCESS,
    payload: {
      ...normalize(payload.component, SCHEMA.quoteItemComponent),
      data: payload.data || {},
    },
  }),
  error: (payload) => ({
    type: types.UPDATE_COMPONENT_ERROR,
    payload,
  }),
};

export const receiveQuoteOpDefs = (opdefs) => ({
  type: types.RECEIVE_QUOTE_OP_DEFS,
  payload: opdefs,
});

export const updateOperationOverrides = {
  error: notify(
    (error) => ({
      type: types.UPDATE_OPERATION_OVERRIDES_ERROR,
      payload: error,
    }),
    "Can't update operation",
  ),
  request: (
    operationId: number,
    overrides: PartialPricingEngineOverrides,
    notes: string | null,
  ) => ({
    type: types.UPDATE_OPERATION_OVERRIDES_REQUEST,
    payload: { operationId, overrides, notes },
  }),
  cancelRequest: ({ operationId }: { operationId: number }) => ({
    type: types.UPDATE_OPERATION_OVERRIDES_CANCEL,
    payload: { operationId },
  }),
};

export const updateOperationIsOutsideService = {
  error: notify(
    (error) => ({
      type: types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_ERROR,
      payload: error,
    }),
    "Can't update operation",
  ),
  request: ({ operationId, isOutsideService }) => ({
    type: types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_REQUEST,
    payload: { operationId, isOutsideService },
  }),
  success: (data) => ({
    type: types.UPDATE_OPERATION_IS_OUTSIDE_SERVICE_SUCCESS,
    payload: normalize(data, SCHEMA.quoteItemOperation),
  }),
};

export const selectFinishOps = {
  request: (payload) => ({
    type: types.SELECT_FINISH_OPS_REQUEST,
    payload,
  }),
  success: (data) => ({
    type: types.SELECT_FINISH_OPS_SUCCESS,
    payload: normalize(data, SCHEMA.quoteItemComponent),
  }),
  error: (payload) => ({
    type: types.SELECT_FINISH_OPS_ERROR,
    payload,
  }),
};

export const fetchCollaborators = (payload) => ({
  type: types.FETCH_COLLABORATORS,
  payload,
});

export const componentUpdatePending = {
  start: () => ({
    type: types.COMPONENT_UPDATE_PENDING_START,
  }),
  finish: () => ({
    type: types.COMPONENT_UPDATE_PENDING_FINISH,
  }),
};

export const createExpedite = {
  request: (payload) => ({
    type: types.CREATE_EXPEDITE_REQUEST,
    payload,
  }),
  success: (payload) => ({
    type: types.CREATE_EXPEDITE_SUCCESS,
    payload: normalize(payload, SCHEMA.quoteItemQuantity),
  }),
  error: (payload) => ({
    type: types.CREATE_EXPEDITE_ERROR,
    payload,
  }),
};

export const updateExpedite = {
  request: (payload) => ({
    type: types.UPDATE_EXPEDITE_REQUEST,
    payload,
  }),
  success: (payload) => ({
    type: types.UPDATE_EXPEDITE_SUCCESS,
    payload: normalize(payload, SCHEMA.quoteItemExpedite),
  }),
  error: (payload) => ({
    type: types.UPDATE_EXPEDITE_ERROR,
    payload,
  }),
};

export const deleteExpedite = {
  request: (payload) => ({
    type: types.DELETE_EXPEDITE_REQUEST,
    payload,
  }),
  success: (payload) => ({
    type: types.DELETE_EXPEDITE_SUCCESS,
    payload: normalize(payload, SCHEMA.quoteItemQuantity),
  }),
  error: (payload) => ({
    type: types.DELETE_EXPEDITE_ERROR,
    payload,
  }),
};

export const updateSupplierMaterial = {
  request: ({
    materialId,
    supplierMaterial,
    partUuid,
    quoteItemId,
    componentId,
  }) => ({
    type: types.UPDATE_SUPPLIER_MATERIAL_REQUEST,
    payload: {
      materialId,
      supplierMaterial,
      partUuid,
      quoteItemId,
      componentId,
    },
  }),
  success: () => ({
    type: types.UPDATE_SUPPLIER_MATERIAL_SUCCESS,
  }),
  error: () => ({
    type: types.UPDATE_SUPPLIER_MATERIAL_ERROR,
  }),
};

export const forceQuoteItemRecalculation = {
  request: ({ quoteItemId, componentId }) => ({
    type: types.FORCE_QUOTE_ITEM_RECALCULATION_REQUEST,
    payload: { quoteItemId, componentId },
  }),
  success: ({ quoteItemId }) => ({
    type: types.FORCE_QUOTE_ITEM_RECALCULATION_SUCCESS,
    payload: { quoteItemId },
  }),
  error: ({ quoteItemId }) => ({
    type: types.FORCE_QUOTE_ITEM_RECALCULATION_ERROR,
    payload: { quoteItemId },
  }),
};

export const forceComponentRegeneration = {
  request: ({ componentId, quoteItemId }) => ({
    type: types.FORCE_COMPONENT_REGENERATION_REQUEST,
    payload: { componentId, quoteItemId },
  }),
  success: ({ componentId, quoteItemId }) => ({
    type: types.FORCE_COMPONENT_REGENERATION_SUCCESS,
    payload: { componentId, quoteItemId },
  }),
  error: ({ componentId, quoteItemId }) => ({
    type: types.FORCE_COMPONENT_REGENERATION_ERROR,
    payload: { componentId, quoteItemId },
  }),
};

export const componentStatuses = {
  receiveProcessingStatuses: ({
    processingStatuses,
  }: {
    processingStatuses: ComponentProcessingStatuses;
  }) => ({
    type: types.RECEIVE_COMPONENT_PROCESSING_STATUSES,
    payload: { processingStatuses },
  }),
  receiveInterrogationStatuses: ({
    interrogationStatuses,
  }: {
    interrogationStatuses: ComponentInterrogationStatuses;
  }) => ({
    type: types.RECEIVE_COMPONENT_INTERROGATION_STATUSES,
    payload: { interrogationStatuses },
  }),
};

export const receiveTemporaryQuoteItemIds = ({ temporaryQuoteItemIds }) => ({
  type: types.RECEIVE_TEMPORARY_QUOTE_ITEM_IDS,
  payload: { temporaryQuoteItemIds },
});

export const receiveImportFromErpTemporaryQuoteItem = ({ taskId }) => ({
  type: types.RECEIVE_IMPORT_FROM_ERP_TEMPORARY_QUOTE_ITEM,
  payload: { taskId },
});

export const receiveImportFromErpTemporaryQuoteItems = ({
  temporaryErpPartQuoteItemIds,
}) => ({
  type: types.RECEIVE_IMPORT_FROM_ERP_TEMPORARY_QUOTE_ITEMS,
  payload: { temporaryErpPartQuoteItemIds },
});

export const addConvertQuoteFileToQuoteItemTasks = ({ tasks }) => ({
  type: types.ADD_CONVERT_QUOTE_FILE_TO_QUOTE_ITEM_TASKS,
  payload: { tasks },
});

export const removeConvertQuoteFileToQuoteItemTasks = ({ tasks }) => ({
  type: types.REMOVE_CONVERT_QUOTE_FILE_TO_QUOTE_ITEM_TASKS,
  payload: { tasks },
});

export const clearConvertSupportingFileToQuoteItemTasks = () => ({
  type: types.CLEAR_CONVERT_QUOTE_FILE_TO_QUOTE_ITEM_TASKS,
});

export const purchasedComponentAdd = {
  request: ({ purchasedComponents, parentComponentId }) => ({
    type: types.PURCHASED_COMPONENT_ADD_REQUEST,
    payload: {
      purchasedComponents,
      parentComponentId,
    },
  }),
  success: ({ briefComponents }) => ({
    type: types.PURCHASED_COMPONENT_ADD_SUCCESS,
    payload: normalize(briefComponents, [SCHEMA.quoteItemBriefComponent]),
  }),
  error: notify(
    (error) => ({
      type: types.PURCHASED_COMPONENT_ADD_ERROR,
      payload: error,
    }),
    'Failed to add purchased components',
  ),
};

export const createQuoteItemWithManualPart = {
  request: () => ({
    type: types.CREATE_QUOTE_ITEM_WITH_MANUAL_PART_REQUEST,
  }),
  success: () => ({
    type: types.CREATE_QUOTE_ITEM_WITH_MANUAL_PART_SUCCESS,
  }),
  error: notify(
    (error) => ({
      type: types.CREATE_QUOTE_ITEM_WITH_MANUAL_PART_ERROR,
      payload: error,
    }),
    'Failed to create quote item with manual part',
  ),
};

export const snapToQuoteItemHeader = {
  request: ({ quoteItemId }) => ({
    type: types.SNAP_TO_QUOTE_ITEM_HEADER_REQUEST,
    payload: { quoteItemId },
  }),
};

export const setQuoteItemsUpdating = {
  request: ({ quoteItemIds }) => ({
    type: types.SET_QUOTE_ITEMS_UPDATING_REQUEST,
    payload: { quoteItemIds },
  }),
  success: ({ quoteItemIds }) => ({
    type: types.SET_QUOTE_ITEMS_UPDATING_SUCCESS,
    payload: { quoteItemIds },
  }),
  error: ({ quoteItemIds }) => ({
    type: types.SET_QUOTE_ITEMS_UPDATING_ERROR,
    payload: { quoteItemIds },
  }),
};

export const resetQuoteItemsStore = () => ({
  type: types.QUOTE_ITEMS_RESET,
});

// TODO: refactor parentComponentIds to be componentIds
export const refreshBomRollup = {
  request: ({
    parentComponentIds,
    delayTime = 500,
    shouldFullyRefreshRootOrExpandedComponents = false,
  }) => ({
    type: types.REFRESH_BOM_ROLLUP_REQUEST,
    payload: {
      parentComponentIds,
      delayTime,
      shouldFullyRefreshRootOrExpandedComponents,
    },
  }),
  finish: () => ({
    type: types.REFRESH_BOM_ROLLUP_FINISHED,
  }),
};

export const updateQuoteItemWorkflowStep = {
  error: notify(
    (error) => ({
      type: types.UPDATE_QUOTE_ITEM_WORKFLOW_STEP_ERROR,
      payload: error,
    }),
    'Failed to save workflow status',
  ),
  request: ({ name, quoteItemId, status, workStepId = null }) => ({
    type: types.UPDATE_QUOTE_ITEM_WORKFLOW_STEP_REQUEST,
    payload: { name, quoteItemId, status, workStepId },
  }),
  success: () => ({
    type: types.UPDATE_QUOTE_ITEM_WORKFLOW_STEP_SUCCESS,
  }),
};

export const receiveProfitItems = (data) => ({
  type: types.RECEIVE_PROFIT_ITEMS,
  payload: normalize(data, [SCHEMA.quoteItemProfitItem]),
});

export const createProfitItem = {
  error: notify(
    (error) => ({
      type: types.PROFIT_ITEM_CREATE_ERROR,
      payload: error,
    }),
    "Can't create Pricing Item",
  ),
  request: ({ componentId, data }) => ({
    type: types.PROFIT_ITEM_CREATE_REQUEST,
    payload: { componentId, data },
  }),
  success: () => ({
    type: types.PROFIT_ITEM_CREATE_SUCCESS,
  }),
};

export const deleteProfitItem = {
  error: notify(
    (error) => ({
      type: types.PROFIT_ITEM_DELETE_ERROR,
      payload: error,
    }),
    "Can't delete Pricing Item",
  ),
  request: ({ profitItemId }) => ({
    type: types.PROFIT_ITEM_DELETE_REQUEST,
    payload: { profitItemId },
  }),
};

export const updateProfitItemCell = {
  error: notify(
    (error) => ({
      type: types.PROFIT_ITEM_CELL_UPDATE_ERROR,
      payload: error,
    }),
    "Can't update Pricing Item cell",
  ),
  request: ({ profitItemCellId, data, cb }) => ({
    type: types.PROFIT_ITEM_CELL_UPDATE_REQUEST,
    payload: { profitItemCellId, data, cb },
  }),
  success: (data) => ({
    type: types.PROFIT_ITEM_CELL_UPDATE_SUCCESS,
    payload: normalize(data, SCHEMA.quoteItemProfitItemCell),
  }),
};

export const updateProfitItem = {
  error: notify(
    (error) => ({
      type: types.PROFIT_ITEM_UPDATE_ERROR,
      payload: error,
    }),
    "Can't update Pricing Item",
  ),
  request: ({ profitItemId, data }) => ({
    type: types.PROFIT_ITEM_UPDATE_REQUEST,
    payload: { profitItemId, data },
  }),
  success: (data) => ({
    type: types.PROFIT_ITEM_UPDATE_SUCCESS,
    payload: normalize(data, SCHEMA.quoteItemProfitItem),
  }),
};

export const updateProfitItemOverrides = {
  error: notify(
    ({ profitItemId }) => ({
      type: types.UPDATE_PROFIT_ITEM_OVERRIDES_ERROR,
      payload: { profitItemId },
    }),
    "Can't update Pricing Item",
  ),
  request: ({ profitItemId, overrides, notes }) => ({
    type: types.UPDATE_PROFIT_ITEM_OVERRIDES_REQUEST,
    payload: { profitItemId, overrides, notes },
  }),
  success: ({ profitItemId }) => ({
    type: types.UPDATE_PROFIT_ITEM_OVERRIDES_SUCCESS,
    payload: { profitItemId },
  }),
};

export const reorderProfitItems = {
  error: notify(
    (error) => ({
      type: types.REORDER_PROFIT_ITEMS_ERROR,
      payload: error,
    }),
    "Can't re-order Pricing Items",
  ),
  request: ({ componentId, oldPosition, nextPosition }) => ({
    type: types.REORDER_PROFIT_ITEMS_REQUEST,
    payload: { componentId, oldPosition, nextPosition },
  }),
  success: () => ({
    type: types.REORDER_PROFIT_ITEMS_SUCCESS,
  }),
};

export const receiveDiscounts = (data) => ({
  type: types.RECEIVE_DISCOUNTS,
  payload: normalize(data, [SCHEMA.quoteItemDiscount]),
});

export const createDiscount = {
  error: notify(
    (error) => ({
      type: types.DISCOUNT_CREATE_ERROR,
      payload: error,
    }),
    "Can't create Discount",
  ),
  request: ({ componentId, data }) => ({
    type: types.DISCOUNT_CREATE_REQUEST,
    payload: { componentId, data },
  }),
  success: () => ({
    type: types.DISCOUNT_CREATE_SUCCESS,
  }),
};

export const deleteDiscount = {
  error: notify(
    (error) => ({
      type: types.DISCOUNT_DELETE_ERROR,
      payload: error,
    }),
    "Can't delete Discount",
  ),
  request: ({ discountId }) => ({
    type: types.DISCOUNT_DELETE_REQUEST,
    payload: { discountId },
  }),
};

export const updateDiscountCell = {
  error: notify(
    (error) => ({
      type: types.DISCOUNT_CELL_UPDATE_ERROR,
      payload: error,
    }),
    "Can't update Discount cell",
  ),
  request: ({ discountCellId, data, cb }) => ({
    type: types.DISCOUNT_CELL_UPDATE_REQUEST,
    payload: { discountCellId, data, cb },
  }),
  success: (data) => ({
    type: types.DISCOUNT_CELL_UPDATE_SUCCESS,
    payload: normalize(data, SCHEMA.quoteItemDiscountCell),
  }),
};

export const updateDiscount = {
  error: notify(
    (error) => ({
      type: types.DISCOUNT_UPDATE_ERROR,
      payload: error,
    }),
    "Can't update Discount",
  ),
  request: ({ discountId, data }) => ({
    type: types.DISCOUNT_UPDATE_REQUEST,
    payload: { discountId, data },
  }),
  success: (data) => ({
    type: types.DISCOUNT_UPDATE_SUCCESS,
    payload: normalize(data, SCHEMA.quoteItemDiscount),
  }),
};

export const updateDiscountOverrides = {
  error: notify(
    ({ discountId }) => ({
      type: types.UPDATE_DISCOUNT_OVERRIDES_ERROR,
      payload: { discountId },
    }),
    "Can't update Discount",
  ),
  request: ({ discountId, overrides, notes }) => ({
    type: types.UPDATE_DISCOUNT_OVERRIDES_REQUEST,
    payload: { discountId, overrides, notes },
  }),
  success: ({ discountId }) => ({
    type: types.UPDATE_DISCOUNT_OVERRIDES_SUCCESS,
    payload: { discountId },
  }),
};

export const reorderDiscounts = {
  error: notify(
    (error) => ({
      type: types.REORDER_DISCOUNTS_ERROR,
      payload: error,
    }),
    "Can't re-order Discounts",
  ),
  request: ({ componentId, oldPosition, nextPosition }) => ({
    type: types.REORDER_DISCOUNTS_REQUEST,
    payload: { componentId, oldPosition, nextPosition },
  }),
  success: () => ({
    type: types.REORDER_DISCOUNTS_SUCCESS,
  }),
};

export const updateMetadata = {
  error: notify(
    (error) => ({
      type: types.UPDATE_METADATA_ERROR,
      payload: error,
    }),
    "Can't update metadata",
  ),
  request: ({ metadata, quoteItemId }) => ({
    type: types.UPDATE_METADATA_REQUEST,
    payload: { metadata, quoteItemId },
  }),
  success: notify(
    ({ quoteItem, data }) => ({
      type: types.UPDATE_METADATA_SUCCESS,
      payload: { quoteItem, data },
    }),
    'Metadata updated.',
  ),
};
